import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, uploadFile } from '../client';

// admin-managed 'document_type' lookup (0037)
export type DocumentType = string;

export interface DocumentRow {
  id: number;
  project_id: number | null;
  client_id: number | null;
  document_type: DocumentType;
  file_path: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  is_confidential: boolean;
  version_number: number;
  parent_document_id: number | null;
  created_at: number;
  updated_at: number;
  created_by: number | null;
  updated_by: number | null;
  // Joined
  project_case_number?: string | null;
  client_name?: string | null;
  created_by_name?: string | null;
}

export interface DocumentsListResponse {
  documents: DocumentRow[];
}

export interface DocumentFilters {
  project_id?: number;
  client_id?: number;
  document_type?: DocumentType;
}

export function useDocuments(filters: DocumentFilters = {}) {
  const params = new URLSearchParams();
  if (filters.project_id !== undefined) params.set('project_id', String(filters.project_id));
  if (filters.client_id !== undefined) params.set('client_id', String(filters.client_id));
  if (filters.document_type !== undefined) params.set('document_type', filters.document_type);
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/documents?${qs}` : '/api/documents';
  return useQuery({
    queryKey: ['documents', { ...filters }],
    queryFn: ({ signal }) => api.get<DocumentsListResponse>(url, signal),
  });
}

export interface UploadDocumentInput {
  file: File;
  document_type: DocumentType;
  project_id?: number | null;
  client_id?: number | null;
  is_confidential?: boolean;
  /** Ask the API to wrap a JPEG/PNG body in a one-page PDF (receipt uploads).
   *  Anything it can't convert is stored as sent — check the returned
   *  `mime_type` rather than assuming a PDF came back. */
  convert_to_pdf?: boolean;
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      file, document_type, project_id, client_id, is_confidential, convert_to_pdf,
    }: UploadDocumentInput) => {
      const params = new URLSearchParams();
      params.set('document_type', document_type);
      if (project_id !== undefined && project_id !== null) {
        params.set('project_id', String(project_id));
      }
      if (client_id !== undefined && client_id !== null) {
        params.set('client_id', String(client_id));
      }
      if (is_confidential) params.set('is_confidential', 'true');
      if (convert_to_pdf) params.set('convert_to_pdf', 'true');
      return uploadFile<{ document: DocumentRow }>(`/api/documents?${params.toString()}`, file);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export interface UploadDocumentVersionInput {
  parent_id: number;
  file: File;
}

export function useUploadDocumentVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parent_id, file }: UploadDocumentVersionInput) =>
      uploadFile<{ document: DocumentRow }>(
        `/api/documents/${parent_id}/new-version`,
        file,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useDocumentVersions(id: number | null) {
  return useQuery({
    queryKey: ['document-versions', { id }],
    queryFn: ({ signal }) =>
      api.get<{ versions: DocumentRow[] }>(`/api/documents/${id}/versions`, signal),
    enabled: id !== null,
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => {
      const qs = reason && reason.trim().length > 0
        ? `?reason=${encodeURIComponent(reason.trim())}`
        : '';
      return api.delete<void>(`/api/documents/${id}${qs}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}
